import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Lock, PartyPopper, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { capacitacionApi, apiError, logout, updateStoredUser } from "../lib/api";
import { ModuleReader } from "../components/capacitaciones/ModuleReader";
import { QuizBox } from "../components/capacitaciones/QuizBox";
import { ModuleGrid, ProgressHeader } from "../components/capacitaciones/ModuleGrid";

const Completed = ({ onEnter }) => (
  <div className="mt-10 rounded-3xl bg-[var(--gold)] text-[#0A1C30] p-6 sm:p-8 flex flex-wrap items-center gap-5" data-testid="cap-completed-banner">
    <PartyPopper className="w-10 h-10" />
    <div className="flex-1 min-w-[220px]">
      <div className="text-2xl font-extrabold">¡Capacitación completada!</div>
      <div className="text-sm font-semibold opacity-80">Ya eres oficialmente parte de la élite de líderes Supera. Tu app está desbloqueada.</div>
    </div>
    <button data-testid="cap-enter-app" onClick={onEnter} className="rounded-xl bg-[#0A1C30] text-white font-extrabold px-6 py-3 hover:brightness-125 transition">Entrar a Supera</button>
  </div>
);

export default function Capacitaciones() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cap, setCap] = useState(null);

  const load = useCallback(() => capacitacionApi.list().then((d) => {
    setData(d);
    updateStoredUser({ requires_onboarding: d.bloqueado });
  }).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const open = async (c) => {
    try { setCap(await capacitacionApi.get(c.id)); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const salir = () => { logout(); navigate("/login", { replace: true }); };

  if (!data) return <div className="min-h-screen bg-navy flex items-center justify-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;
  const done = data.total > 0 && data.aprobadas >= data.total;

  return (
    <div className="min-h-screen bg-navy relative overflow-hidden" data-testid="capacitaciones-page">
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[var(--gold)]/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <div className="flex items-center gap-3 mb-10">
          {cap ? (
            <button data-testid="cap-back" onClick={() => { setCap(null); load(); }} className="inline-flex items-center gap-2 text-sm font-bold text-white/70 hover:text-white"><ArrowLeft className="w-4 h-4" /> Módulos</button>
          ) : !data.bloqueado ? (
            <button data-testid="cap-home" onClick={() => navigate("/mi-cuenta")} className="inline-flex items-center gap-2 text-sm font-bold text-white/70 hover:text-white"><ArrowLeft className="w-4 h-4" /> Mi cuenta</button>
          ) : (
            <span className="inline-flex items-center gap-2 text-xs font-bold text-white/60" data-testid="cap-lock-notice"><Lock className="w-4 h-4 text-[var(--gold)]" /> Completa tu capacitación para desbloquear la app</span>
          )}
          <button data-testid="cap-logout" onClick={salir} className="ml-auto inline-flex items-center gap-2 text-sm font-bold text-white/60 hover:text-white"><LogOut className="w-4 h-4" /> Salir</button>
        </div>

        {cap ? (
          <div className="rounded-3xl bg-white p-5 sm:p-8 lg:p-10">
            <ModuleReader cap={cap} />
            <QuizBox key={cap.id} preguntas={cap.preguntas} aprobadoPrevio={cap.progreso?.status === "aprobado"}
              onSubmit={(r) => capacitacionApi.evaluar(cap.id, r)}
              onPassed={(r) => { if (r.completo) updateStoredUser({ requires_onboarding: false }); }} />
          </div>
        ) : (
          <>
            <ProgressHeader aprobadas={data.aprobadas} total={data.total} title="Capacitación Supera"
              subtitle="Domina las herramientas de tu programa como un líder de élite. Lee cada módulo y aprueba su evaluación (4 de 5)." />
            {done && <Completed onEnter={() => navigate("/mi-cuenta")} />}
            <ModuleGrid items={data.items} isDone={(c) => c.progreso?.status === "aprobado"} onOpen={open} />
          </>
        )}
      </div>
    </div>
  );
}
